import { AlertTriangle } from 'lucide-react';
import type { CapDebug } from './types';

// Live cap decomposition for one trader. Answers WHY the cap `current` is what
// it is: the split across kalshi open / poly open / in-flight intents, and the
// settled-market exclude-set size (inactiveExcluded). An empty exclude set while
// at/near the cap is the false-reject signature.
export function CapInspector({
  cap,
  err,
  loading,
  onClose,
}: {
  cap: CapDebug | null;
  err: string | null;
  loading: boolean;
  onClose: () => void;
}) {
  const num = (n: number) => n.toLocaleString();
  const atContractCap =
    cap != null && cap.caps.maxContracts != null && cap.committedContracts >= cap.caps.maxContracts;
  const nearContractCap =
    cap != null &&
    cap.caps.maxContracts != null &&
    cap.committedContracts >= cap.caps.maxContracts * 0.9;
  // The known false-reject signature: pressed against the contract cap while the
  // settled-market exclude set is empty — if the trader holds resolved markets,
  // those are counting because the /markets read that clears them came back empty.
  const suspectExclusionMiss = nearContractCap && cap != null && cap.kalshi.inactiveExcluded === 0;

  return (
    <div className="border-border bg-muted/30 mt-3 rounded-lg border p-4">
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-foreground text-sm font-semibold">
          Cap inspector{cap ? ` — ${cap.username ?? cap.userId}` : ''}
          {cap?.orgName ? <span className="text-muted-foreground ml-1 font-normal">· {cap.orgName}</span> : null}
        </h3>
        <button
          onClick={onClose}
          className="border-border text-muted-foreground hover:text-foreground ml-auto rounded border px-2 py-0.5 text-[11px]"
        >
          close
        </button>
      </div>

      {err ? (
        <p className="py-2 text-center text-xs text-red-400">Couldn&apos;t load: {err}</p>
      ) : !cap ? (
        <p className="text-muted-foreground py-2 text-center text-xs">{loading ? 'Reading live cap state…' : '—'}</p>
      ) : (
        <div className="space-y-3 text-xs">
          {suspectExclusionMiss && (
            <div className="flex items-start gap-2 rounded bg-amber-500/10 px-3 py-2 text-amber-300">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>
                At/near the contract cap with <span className="font-semibold">0 settled markets excluded</span>. If
                this trader holds resolved/voided markets, they&apos;re still counting toward the cap — the settled-
                exclusion read (Kalshi /markets) likely came back empty. This is the known false-reject bug; it bites
                hardest during a morning rate-limit crunch. Re-open this during the failure window to confirm.
              </span>
            </div>
          )}

          {/* Contract cap decomposition — the headline. */}
          <div>
            <div className="text-muted-foreground mb-1 text-[11px] font-medium uppercase tracking-wide">
              Contract cap
            </div>
            <div className="font-mono">
              <span className={atContractCap ? 'font-semibold text-red-400' : 'text-foreground'}>
                {num(cap.committedContracts)}
              </span>
              <span className="text-muted-foreground"> / {cap.caps.maxContracts == null ? '∞' : num(cap.caps.maxContracts)}</span>
              <span className="text-muted-foreground">
                {'  ='} kalshi open {num(cap.kalshi.openContracts)} + poly open {num(cap.poly.openContracts)} + intents{' '}
                {num(cap.intentContracts)}
              </span>
            </div>
          </div>

          {/* Position cap decomposition. */}
          <div>
            <div className="text-muted-foreground mb-1 text-[11px] font-medium uppercase tracking-wide">
              Position cap
            </div>
            <div className="font-mono">
              <span className="text-foreground">{num(cap.committedPositions)}</span>
              <span className="text-muted-foreground"> / {cap.caps.maxPositions == null ? '∞' : num(cap.caps.maxPositions)}</span>
              <span className="text-muted-foreground">
                {'  '}(distinct ticker|side across venues + intents)
              </span>
            </div>
          </div>

          {/* Settled-exclusion health per venue. */}
          <div>
            <div className="text-muted-foreground mb-1 text-[11px] font-medium uppercase tracking-wide">
              Settled-market exclusion (health)
            </div>
            <table className="w-full text-left font-mono">
              <thead className="text-muted-foreground border-border border-b">
                <tr>
                  <th className="py-1 pr-3 font-medium">Venue</th>
                  <th className="px-3 text-right font-medium" title="net contracts counting toward the cap">Open ct</th>
                  <th className="px-3 text-right font-medium" title="distinct open ticker|side">Open pos</th>
                  <th className="px-3 text-right font-medium" title="settled/voided tickers excluded from the cap">Excluded</th>
                  <th className="px-3 text-right font-medium" title="all tickers ever in the fills ledger">Universe</th>
                </tr>
              </thead>
              <tbody>
                {(['kalshi', 'poly'] as const).map((v) => {
                  const ven = cap[v];
                  const empty = ven.inactiveExcluded === 0 && ven.tickerUniverse > ven.openPositions;
                  return (
                    <tr key={v} className="border-border/50 border-b">
                      <td className="text-foreground py-1 pr-3">{v}</td>
                      <td className="text-foreground px-3 text-right">{num(ven.openContracts)}</td>
                      <td className="text-foreground px-3 text-right">{num(ven.openPositions)}</td>
                      <td className={`px-3 text-right ${empty ? 'font-semibold text-amber-400' : 'text-muted-foreground'}`}>
                        {num(ven.inactiveExcluded)}
                      </td>
                      <td className="text-muted-foreground px-3 text-right">{num(ven.tickerUniverse)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Biggest contributors — eyeball what's actually counting. */}
          {cap.kalshi.positions.length > 0 && (
            <div>
              <div className="text-muted-foreground mb-1 text-[11px] font-medium uppercase tracking-wide">
                Top Kalshi positions counting toward the cap ({cap.kalshi.openPositions})
              </div>
              <div className="max-h-48 overflow-y-auto">
                <table className="w-full text-left font-mono">
                  <tbody>
                    {cap.kalshi.positions.map((p, i) => (
                      <tr key={`${p.ticker}-${p.side}-${i}`} className="border-border/50 border-b">
                        <td className="text-foreground py-0.5 pr-3">{p.ticker}</td>
                        <td className="text-muted-foreground px-3">{p.side}</td>
                        <td className="text-foreground px-3 text-right">{num(p.netCount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <p className="text-muted-foreground text-[11px]">
            Live snapshot, server clock {new Date(cap.serverTs).toLocaleTimeString()}. Reading this triggers a fresh
            Kalshi /markets read, so mid-day it usually reads healthy — the durable reject breach above is the record
            of what actually happened during the failure.
          </p>
        </div>
      )}
    </div>
  );
}
